import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useKlinischerBefund } from "../../context/KlinischerBefundContext";
import { useAllgemeineDaten } from "../../context/AllgemeineDatenContext";
import styles from "./KlinischerBefund.module.css";

const KlinischerBefund = () => {
  const navigate = useNavigate();
  const { data, setData } = useKlinischerBefund();
  const [errors, setErrors] = useState({});

  const { data: allgemeineData } = useAllgemeineDaten();
  const fussAuswahl = allgemeineData?.fussAuswahl;

// Ensure PNP fields are never undefined (prevents edge cases)

useEffect(() => {
  window.scrollTo(0, 0);
}, []);

useEffect(() => {
  setData((prev) => {
    let changed = false;
    const newData = { ...prev };

    if (newData.linksHinweisPNP === undefined) {
      newData.linksHinweisPNP = "";
      changed = true;
    }
    if (newData.rechtsHinweisPNP === undefined) {
      newData.rechtsHinweisPNP = "";
      changed = true;
    }

    return changed ? newData : prev;
  });
}, []);  // only once on mount


useEffect(() => {
  setData((prev) => {
    const newData = { ...prev };
    let changed = false;

    ["links", "rechts"].forEach((side) => {
      const mikro = newData[`${side}Mikrofilament`];
      const vibStr = newData[`${side}Vibrationsempfindung`];
      const vib = vibStr === "" ? null : Number(vibStr);

      const shouldForceNo  = mikro === "ja"  && vib !== null && vib >= 4;
      const shouldForceYes = mikro === "nein" && vib !== null && vib <= 3;

      if (shouldForceNo && newData[`${side}HinweisPNP`] !== "nein") {
        newData[`${side}HinweisPNP`] = "nein";
        changed = true;
      }
      else if (shouldForceYes && newData[`${side}HinweisPNP`] !== "ja") {
        newData[`${side}HinweisPNP`] = "ja";
        changed = true;
      }
    });

    return changed ? newData : prev;
  });
}, [
  data.linksMikrofilament,
  data.rechtsMikrofilament,
  data.linksVibrationsempfindung,
  data.rechtsVibrationsempfindung,
]);

// Make sure PNP is always string
useEffect(() => {
  setData(prev => ({
    ...prev,
    linksHinweisPNP: prev.linksHinweisPNP ?? "",
    rechtsHinweisPNP: prev.rechtsHinweisPNP ?? "",
  }));
}, []);

// Reset PNP validation errors whenever PNP value changes (after auto-force or manual selection)
useEffect(() => {
  setErrors((prevErrors) => {
    const updatedErrors = { ...prevErrors };

    // If PNP now has a value (auto or manual), remove the error
    if (data.linksHinweisPNP) {
      delete updatedErrors.linksHinweisPNP;
    }
    if (data.rechtsHinweisPNP) {
      delete updatedErrors.rechtsHinweisPNP;
    }

    return updatedErrors;
  });
}, [data.linksHinweisPNP, data.rechtsHinweisPNP]);

const handleChange = (e) => {
  const { name, value, type } = e.target;

  setData((prev) => {
    const newData = {
      ...prev,
      [name]: type === "radio" ? value : value,
    };

// Auto-logic for Hinweis PNP
const sides = ["links", "rechts"];
sides.forEach((side) => {
  const mikro = newData[`${side}Mikrofilament`];
  const vibStr = newData[`${side}Vibrationsempfindung`];
  const vib = vibStr === "" ? null : Number(vibStr);

  const hasGoodMicro = mikro === "ja";
  const hasGoodVib   = vib !== null && vib >= 4;
  const hasBadMicro  = mikro === "nein";
  const hasBadVib    = vib !== null && vib <= 3;

  let shouldBe = null;

  if (hasGoodMicro && hasGoodVib) {
    shouldBe = "nein";
  } else if (hasBadMicro && hasBadVib) {
    shouldBe = "ja";
  }

const shouldForceNo  = mikro === "ja"  && vib !== null && vib >= 4;
const shouldForceYes = mikro === "nein" && vib !== null && vib <= 3;

if (shouldForceNo && newData[`${side}HinweisPNP`] !== "nein") {
  newData[`${side}HinweisPNP`] = "nein";
}
else if (shouldForceYes && newData[`${side}HinweisPNP`] !== "ja") {
  newData[`${side}HinweisPNP`] = "ja";
}
});

    return newData;
  });

  let err = null;
  if (name === "linksVibrationsempfindung") {
    const v = Number(value);
    if (value !== "" && (v < 0 || v > 8)) {
      err = "Wert muss zwischen 0 und 8 liegen";
    }
  }
  if (name === "rechtsVibrationsempfindung") {
    const v = Number(value);
    if (value !== "" && (v < 0 || v > 8)) {
      err = "Wert muss zwischen 0 und 8 liegen";
    }
  }
  setErrors((prev) => ({ ...prev, [name]: err }));
};

const isPNPControlled = (side) => {
  const mikro = data[`${side}Mikrofilament`];
  const vibStr = data[`${side}Vibrationsempfindung`];
  const vib = vibStr === "" ? null : Number(vibStr);

  const hasGoodMicro = mikro === "ja";
  const hasGoodVib   = vib !== null && vib >= 4;
  const hasBadMicro  = mikro === "nein";
  const hasBadVib    = vib !== null && vib <= 3;

  return (hasGoodMicro && hasGoodVib) || (hasBadMicro && hasBadVib);
};

const handleManualPNPChange = (side, value) => {
  if (isPNPControlled(side)) return; // safety: don't allow change when blocked
  setData((prev) => ({
    ...prev,
    [`${side}HinweisPNP`]: value,
  }));
};

const validateForm = () => {
  const newErrors = {};
  
  // Only validate LEFT foot fields when shown
  if (showLeftFoot) {
    if (!data.linksMikrofilament)
      newErrors.linksMikrofilament = "Bitte wählen Sie eine Option";
    if (data.linksVibrationsempfindung === "")
      newErrors.linksVibrationsempfindung = "Bitte Wert eingeben";
    if (!data.linksHinweisPNP)
      newErrors.linksHinweisPNP = "Bitte wählen Sie eine Option";
    if (!data.linksHinweisPAVK)
      newErrors.linksHinweisPAVK = "Bitte wählen Sie eine Option";
  }
  
  // Only validate RIGHT foot fields when shown
  if (showRightFoot) {
    if (!data.rechtsMikrofilament)
      newErrors.rechtsMikrofilament = "Bitte wählen Sie eine Option";
    if (data.rechtsVibrationsempfindung === "")
      newErrors.rechtsVibrationsempfindung = "Bitte Wert eingeben";
    if (!data.rechtsHinweisPNP)
      newErrors.rechtsHinweisPNP = "Bitte wählen Sie eine Option";
    if (!data.rechtsHinweisPAVK)
      newErrors.rechtsHinweisPAVK = "Bitte wählen Sie eine Option";
  }
  
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      navigate("/fussstatus");
    }
  };

const showLeftFoot = fussAuswahl !== "rechts";
const showRightFoot = fussAuswahl !== "links";
const showBothFeet = showLeftFoot && showRightFoot;

  return (
    <div className={styles["klinischer-befund-page"]}>
      <h1>Klinischer Befund</h1>
      <form className={styles["form-horizontal"]} onSubmit={handleSubmit}>
        
{/* Titles for desktop */}
<div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
  <div className={styles["col-sm-4"]}></div>
  {showLeftFoot && (
    <div className={styles["col-sm-4"]}>
      <h3 className={`${styles.footTitle} ${styles.desktopOnly}`}>Linker Fuß</h3>
    </div>
  )}
  {showRightFoot && (
    <div className={styles["col-sm-4"]}>
      <h3 className={`${styles.footTitle} ${styles.desktopOnly}`}>Rechter Fuß</h3>
    </div>
  )}
</div>

        {/* ROW 1 — MIKROFILAMENT */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>
              Mikrofilament-Erkennung{" "}
              <span className={styles["small-label"]}>
                (am Met.-köpfchen 2 plantar)
              </span>
            </label>
          </div>
          {/* LINKS */}
           {showLeftFoot && (
          <div className={styles["col-sm-4"]}>
            <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>Linker Fuß</h3>
            <div className={styles["radio-group"]}>
              {["ja", "nein"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="linksMikrofilament"
                    value={v}
                    checked={data.linksMikrofilament === v}
                    onChange={handleChange}
                  />
                  {v}
                </label>
              ))}
            </div>
            <p className={styles.hintText}>(nein = PNP-Anzeichen)</p>
            {errors.linksMikrofilament && (
              <p className={styles["error-text"]}>{errors.linksMikrofilament}</p>
            )}
          </div>
                    )}
          {/* RECHTS */}
          {showRightFoot && (
          <div className={styles["col-sm-4"]}>
            <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>Rechter Fuß</h3>
            <div className={styles["radio-group"]}>
              {["ja", "nein"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="rechtsMikrofilament"
                    value={v}
                    checked={data.rechtsMikrofilament === v}
                    onChange={handleChange}
                  />
                  {v}
                </label>
              ))}
            </div>
            <p className={styles.hintText}>(nein = PNP-Anzeichen)</p>
            {errors.rechtsMikrofilament && (
              <p className={styles["error-text"]}>{errors.rechtsMikrofilament}</p>
            )}
          </div>
                    )}
        </div>

        <div className={styles.sectionDivider}></div>

        {/* ROW 2 — VIBRATION */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>
              Vibrationsempfindung{" "}
              <span className={styles["small-label"]}>(am D1-Grundgelenk)</span>
            </label>
          </div>
          {/* LINKS */}
          {showLeftFoot && (
          <div className={styles["col-sm-4"]}>
            <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>Linker Fuß</h3>
            <input
              type="number"
              name="linksVibrationsempfindung"
              className={styles.input}
              placeholder=".../8"
              min="0"
              max="8"
              value={data.linksVibrationsempfindung}
              onChange={handleChange}
            />
            <p className={styles.vibrationNote}>(&lt; 4/8 = PNP-Anzeichen)</p>
            {errors.linksVibrationsempfindung && (
              <p className={styles["error-text"]}>
                {errors.linksVibrationsempfindung}
              </p>
            )}
          </div>
                    )}
          {/* RECHTS */}
          {showRightFoot && (
          <div className={styles["col-sm-4"]}>
            <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>Rechter Fuß</h3>
            <input
              type="number"
              name="rechtsVibrationsempfindung"
              className={styles.input}
              placeholder=".../8"
              min="0"
              max="8"
              value={data.rechtsVibrationsempfindung}
              onChange={handleChange}
            />
            <p className={styles.vibrationNote}>(&lt; 4/8 = PNP-Anzeichen)</p>
            {errors.rechtsVibrationsempfindung && (
              <p className={styles["error-text"]}>
                {errors.rechtsVibrationsempfindung}
              </p>
            )}
          </div>
                    )}
        </div>

        <div className={styles.sectionDivider}></div>

        {/* ROW 3 — PNP */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>
              Hinweis PNP{" "}
              <span className={styles["small-label"]}>(Sensibilitätsverlust)</span>
            </label>
          </div>
      {/* LINKS */}
      {showLeftFoot && (
      <div className={styles["col-sm-4"]}>
        <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>Linker Fuß</h3>
        <div className={styles["radio-group"]}>
          {["ja", "nein"].map((v) => {
            const controlled = isPNPControlled("links");
            const currentValue = data.linksHinweisPNP ?? "";

            return (
              <label key={v} className={styles["radio-inline"]}>
                <input
                  type="radio"
                  name="linksHinweisPNP"
                  value={v}
                  checked={currentValue === v}
                  onChange={(e) => handleManualPNPChange("links", e.target.value)}
                  disabled={controlled}
                  style={controlled ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                />
                {v}
              </label>
            );
          })}
        </div>

        {isPNPControlled("links") && (
          <p className={`${styles.hintText} ${styles["auto-info"]}`}>
            {data.linksHinweisPNP === "ja"
              ? "Automatisch „ja“ wegen Mikrofil. nein und Vibration ≤ 3"
              : "Automatisch „nein“ wegen Mikrofil. ja und Vibration ≥ 4"}
          </p>
        )}

        {errors.linksHinweisPNP && (
          <p className={styles["error-text"]}>{errors.linksHinweisPNP}</p>
        )}
      </div>
            )}
      {/* RECHTS */}
      {showRightFoot && (
      <div className={styles["col-sm-4"]}>
        <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>Rechter Fuß</h3>
        <div className={styles["radio-group"]}>
          {["ja", "nein"].map((v) => {
            const controlled = isPNPControlled("rechts");
            const currentValue = data.rechtsHinweisPNP ?? "";

            return (
              <label key={v} className={styles["radio-inline"]}>
                <input
                  type="radio"
                  name="rechtsHinweisPNP"
                  value={v}
                  checked={currentValue === v}
                  onChange={(e) => handleManualPNPChange("rechts", e.target.value)}
                  disabled={controlled}
                  style={controlled ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                />
                {v}
              </label>
            );
          })}
        </div>

        {isPNPControlled("rechts") && (
          <p className={`${styles.hintText} ${styles["auto-info"]}`}>
            {data.rechtsHinweisPNP === "ja"
              ? "Automatisch „ja“ wegen Mikrofil. nein und Vibration ≤ 3"
              : "Automatisch „nein“ wegen Mikrofil. ja und Vibration ≥ 4"}
          </p>
        )}

        {errors.rechtsHinweisPNP && (
          <p className={styles["error-text"]}>{errors.rechtsHinweisPNP}</p>
        )}
      </div>
            )}
        </div>

        <div className={styles.sectionDivider}></div>

        {/* ROW 4 — PAVK */}
        <div className={`${styles.row} ${!showBothFeet ? styles['single-column'] : ''}`}>
          <div className={styles["col-sm-4"]}>
            <label className={styles.label}>
              Hinweis PAVK{" "}
              <span className={styles["small-label"]}>
                (periphere arterielle Verschlusskrankheit)
              </span>
            </label>
          </div>
          {/* LINKS */}
          {showLeftFoot && (
          <div className={styles["col-sm-4"]}>
            <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>Linker Fuß</h3>
            <div className={styles["radio-group"]}>
              {["ja", "nein"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="linksHinweisPAVK"
                    value={v}
                    checked={data.linksHinweisPAVK === v}
                    onChange={handleChange}
                  />
                  {v}
                </label>
              ))}
            </div>
            {errors.linksHinweisPAVK && (
              <p className={styles["error-text"]}>{errors.linksHinweisPAVK}</p>
            )}
          </div>
                    )}
          {/* RECHTS */}
          {showRightFoot && (
          <div className={styles["col-sm-4"]}>
            <h3 className={`${styles.footTitle} ${styles.mobileOnly}`}>Rechter Fuß</h3>
            <div className={styles["radio-group"]}>
              {["ja", "nein"].map((v) => (
                <label key={v} className={styles["radio-inline"]}>
                  <input
                    type="radio"
                    name="rechtsHinweisPAVK"
                    value={v}
                    checked={data.rechtsHinweisPAVK === v}
                    onChange={handleChange}
                  />
                  {v}
                </label>
              ))}
            </div>
            {errors.rechtsHinweisPAVK && (
              <p className={styles["error-text"]}>{errors.rechtsHinweisPAVK}</p>
            )}
          </div>
                    )}
        </div>

        {/* ACTIONS */}
        <div className={`${styles.row} ${styles.actions}`}>
          <button
            type="button"
            className={styles["back-btn"]}
            onClick={() => navigate("/allgemeineDaten")}
          >
            Zurück
          </button>
          <input
            type="submit"
            value="Weiter"
            className={styles["btn-primary"]}
          />
        </div>
      </form>
    </div>
  );
};

export default KlinischerBefund;